import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useBottomNavTarget } from '../lib/useBottomNavTarget';
import { isAdminPath } from '../lib/routes';
import { useNotifications } from '../context/NotificationsContext';
import WeatherWidget from './WeatherWidget';

function GlobalNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const fullWidth = isAdminPath(pathname);
  const { user, isAuthenticated, isAdmin, logout, getToken } = useAuth();
  const [councilSlug, setCouncilSlug] = useState(null);
  const { slug: condoSlug, currentTab } = useBottomNavTarget();
  const { unreadCount } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  // The Council link shows only where the self-management switch lets this user in.
  useEffect(() => {
    if (!isAuthenticated || !getToken) {
      setCouncilSlug(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(axios.get(`${getApiBaseUrl()}/api/council`, { headers: { Authorization: `Bearer ${getToken()}` } }))
      .then((response) => { if (!cancelled) setCouncilSlug(response?.data?.condos?.[0]?.slug || null); })
      .catch(() => { if (!cancelled) setCouncilSlug(null); });
    return () => { cancelled = true; };
  }, [isAuthenticated, getToken, user?._id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="bg-card border-b border-border pt-[env(safe-area-inset-top)]">
      <div className={fullWidth ? 'px-4 md:px-8' : 'container mx-auto px-4'}>
        <div className={fullWidth ? undefined : 'max-w-7xl mx-auto'}>
          <div className="flex justify-between items-center h-16">
          <Link
            to={isAuthenticated && user?.condos?.length > 0 ? `/${user.condos[0].slug || user.condos[0]}` : "/"}
            className="min-w-0 shrink"
          >
            <img src="/logo.svg" alt="Laftel Condo" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            {isAuthenticated ? (
              <>
                {condoSlug && (
                  <WeatherWidget condoSlug={condoSlug} active={currentTab === 'weather'} />
                )}
                {condoSlug && (
                  <Link
                    to={`/${condoSlug}/notifications`}
                    aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                    className={`relative p-2 rounded-lg transition-colors hover:bg-muted ${
                      currentTab === 'notifications' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadCount > 0 && (
                      <span
                        aria-hidden="true"
                        className="absolute top-0.5 right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full bg-notificationBadge text-notificationBadge-foreground text-[0.625rem] font-bold leading-none"
                      >
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                <div className="relative" ref={menuRef} data-l-private>
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={user?.name ? `Account menu for ${user.name}` : 'Account menu'}
                    className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 text-muted-foreground transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-54 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                      <div className="px-4 py-4 border-b border-border">
                        <p className="text-sm font-medium text-foreground">{user?.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 break-words">{user?.email}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-3">
                          {user?.condos?.map((c) => (
                            <span
                              key={c._id || c}
                              className="text-xs px-2 py-0.5 rounded bg-secondary text-foreground"
                            >
                              {c.name || c.slug || c}
                            </span>
                          ))}
                          <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary capitalize">
                            {user?.role}
                          </span>
                          {isAdmin && (
                            <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-500">
                              Admin
                            </span>
                          )}
                        </div>
                      </div>

                      <Link
                        to="/account"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Account
                      </Link>

                      {councilSlug && (
                        <Link
                          to={`/council/${councilSlug}`}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Council
                        </Link>
                      )}

                      {isAdmin && (
                        <Link
                          to="/admin?resume=1"
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Admin
                        </Link>
                      )}

                      <div className="border-t border-border mt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-destructive hover:bg-muted transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-muted-foreground hover:text-foreground px-2 py-2 sm:px-0"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="text-sm bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90"
                >
                  Register
                </Link>
              </>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GlobalNav;
